/**
 * Контекст для управления состоянием онлайн чата
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import ChatAPI from '../../services/ChatAPI';

const ChatContext = createContext();

export const useChatContext = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChatContext должен использоваться внутри ChatProvider');
    }
    return context;
};

export const ChatProvider = ({ children }) => {
    const INACTIVITY_MS = 20 * 60 * 1000; // 20 минут

    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'system',
            message: 'Welcome to online support! How can we help you?',
            created_at: new Date().toISOString()
        }
    ]);
    const [isOperatorOnline, setIsOperatorOnline] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
    const [autoReplyTimer, setAutoReplyTimer] = useState(null);
    const inactivityTimerRef = React.useRef(null);

    // Загрузка истории сообщений при открытии чата
    useEffect(() => {
        if (isOpen) {
            loadMessages();
        }
    }, [isOpen]);

    // Подписка на новые сообщения (ответы оператора)
    useEffect(() => {
        if (isOpen) {
            const unsubscribe = ChatAPI.subscribeToMessages((newMessages) => {
                // Обновляем сообщения, добавляя системное приветствие в начало
                const formattedMessages = newMessages.map(msg => ({
                    id: msg.id,
                    type: msg.type,
                    message: msg.message,
                    created_at: msg.created_at
                }));

                setMessages([
                    {
                        id: 0,
                        type: 'system',
                        message: 'Welcome to online support! How can we help you?',
                        created_at: new Date().toISOString()
                    },
                    ...formattedMessages
                ]);
            });

            return () => unsubscribe();
        }
    }, [isOpen]);

    // Проверка статуса сервера
    useEffect(() => {
        const checkStatus = async () => {
            const status = await ChatAPI.checkStatus();
            setIsOperatorOnline(status.status === 'online');
        };

        checkStatus();
        const interval = setInterval(checkStatus, 30000); // Проверяем каждые 30 секунд

        return () => clearInterval(interval);
    }, []);

    const loadMessages = async () => {
        try {
            setIsLoading(true);
            const historyMessages = await ChatAPI.getMessages();
            
            if (historyMessages.length > 0) {
                const formattedMessages = historyMessages.map(msg => ({
                    id: msg.id,
                    type: msg.type,
                    message: msg.message,
                    created_at: msg.created_at
                }));

                setMessages([
                    {
                        id: 0,
                        type: 'system',
                        message: 'Welcome to online support! How can we help you?',
                        created_at: new Date().toISOString()
                    },
                    ...formattedMessages
                ]);
            }
        } catch (error) {
            console.error('Ошибка загрузки сообщений:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const openChat = useCallback(() => {
        setIsOpen(true);
    }, []);

    const closeChat = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Сбрасывает таймер неактивности — вызывается после каждого сообщения пользователя
    const resetInactivityTimer = useCallback(() => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }
        inactivityTimerRef.current = setTimeout(async () => {
            await ChatAPI.deleteSession();
            setMessages([
                {
                    id: Date.now(),
                    type: 'system',
                    message: '🕐 Chat closed due to 20 minutes of inactivity. Start a new conversation anytime.',
                    created_at: new Date().toISOString()
                }
            ]);
            setHasUserSentMessage(false);
            if (autoReplyTimer) {
                clearTimeout(autoReplyTimer);
                setAutoReplyTimer(null);
            }
        }, INACTIVITY_MS);
    }, [INACTIVITY_MS, autoReplyTimer]);

    const addMessage = useCallback(async (messageData) => {
        const newMessage = {
            id: Date.now(),
            type: messageData.type || 'user',
            message: messageData.text || messageData.message,
            created_at: new Date().toISOString()
        };

        // Добавляем сообщение локально сразу
        setMessages(prev => [...prev, newMessage]);

        // Отправляем на сервер только сообщения пользователя
        if (newMessage.type === 'user') {
            try {
                await ChatAPI.sendMessage(newMessage.message);
                resetInactivityTimer();
                
                // Если это первое сообщение пользователя, запускаем таймер автоответа
                if (!hasUserSentMessage) {
                    setHasUserSentMessage(true);
                    
                    // Очищаем предыдущий таймер если он есть
                    if (autoReplyTimer) {
                        clearTimeout(autoReplyTimer);
                    }
                    
                    // Отправляем автоответ через 5 секунд
                    const timer = setTimeout(() => {
                        setMessages(prev => [...prev, {
                            id: Date.now(),
                            type: 'system',
                            message: '⏱️ Please wait ~3 minutes. Our operator will respond to you shortly!',
                            created_at: new Date().toISOString()
                        }]);
                    }, 5000);
                    
                    setAutoReplyTimer(timer);
                }
            } catch (error) {
                console.error('Ошибка отправки сообщения:', error);
                // Можно добавить системное сообщение об ошибке
                setMessages(prev => [...prev, {
                    id: Date.now(),
                    type: 'system',
                    message: '⚠️ Error sending message. Please try again.',
                    created_at: new Date().toISOString()
                }]);
            }
        }
    }, [hasUserSentMessage, autoReplyTimer, resetInactivityTimer]);

    const clearMessages = useCallback(() => {
        setMessages([
            {
                id: 1,
                type: 'system',
                message: 'Welcome to online support! How can we help you?',
                created_at: new Date().toISOString()
            }
        ]);
        setHasUserSentMessage(false);
        if (autoReplyTimer) {
            clearTimeout(autoReplyTimer);
            setAutoReplyTimer(null);
        }
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
    }, [autoReplyTimer]);

    const value = {
        isOpen,
        messages,
        isOperatorOnline,
        isLoading,
        openChat,
        closeChat,
        addMessage,
        clearMessages,
        setIsOperatorOnline,
        resetInactivityTimer
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};
